import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import TableTabs from 'components/TableTabs';
import { Widget } from '@spins/react-ui-library';
import StoreItemTable from 'components/Groups/StoreItemTable';
import { getData } from 'utils/functions';
import { CONTEXT, GroupType } from 'utils/types';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector,
} from 'store/global/selectors';
import * as selectors from 'containers/Groups/selectors';
import * as actions from 'containers/Groups/actions';
import Alert from 'react-bootstrap/Alert';
import * as groupModalActions from 'containers/PageHeader/Groups/store/actions';
import { STORES_GROUPS_TYPE, ITEMS_GROUPS_TYPE, TAB_TO_QUERY_PARAM } from 'utils/types';
import { getGroupDetailsNavigation } from 'utils/functions';

interface Props {
  storesData: any;
  storesLoading: boolean;
  storesError: any;
  itemsData: any;
  itemsLoading: boolean;
  itemsError: any;
  currentTab: string;
  errorMessage?: string;
  onEditOpen(id: string, string: GroupType): void;
  onTabSelect(tab: string): void;
  onErrorMessageClose(): void;
  onViewGroup(id: string): void;
  onDeleteGroup(id: string, context: string): void;
}

const GroupsTablesWidget: React.FC<Props> = ({
  storesData,
  storesLoading,
  storesError,
  itemsData,
  itemsLoading,
  itemsError,
  currentTab,
  errorMessage,
  onEditOpen,
  onTabSelect,
  onErrorMessageClose,
  onViewGroup,
  onDeleteGroup,
}) => {
  const history = useHistory();

  const [storeGroupsTotal, setStoreGroupsTotal] = useState(0);
  const [itemGroupsTotal, setItemGroupsTotal] = useState(0);

  useEffect(() => {
    if (!storesLoading && !storesError && storesData) {
      setStoreGroupsTotal(getData(storesData).length);
    }
    if (!itemsLoading && !itemsError && itemsData) {
      setItemGroupsTotal(getData(itemsData).length);
    }
  }, [storesLoading, storesError, storesData, itemsLoading, itemsError, itemsData]);

  const handleActionClick = (key: string, id: string, groupType: GroupType) => {
    switch (key) {
      case 'Edit':
        onEditOpen(id, groupType);
        break;
      case 'View':
        onViewGroup(id);
        break;
      case 'Delete':
        onDeleteGroup(id, `${CONTEXT}-${groupType}-group`);
        break;
    }
  };

  const handleTabSelect = (value: string) => {
    onTabSelect(value);
  };

  return (
    <Widget>
      {{
        header: {
          content: {
            left: (
              <TableTabs
                selectedTab={currentTab}
                tabs={[
                  { name: 'Store Groups', total: storeGroupsTotal },
                  { name: 'Item Groups', total: itemGroupsTotal },
                ]}
                onSelect={handleTabSelect}
              />
            ),
          },
        },
        body: {
          content: (
            <>
              {errorMessage && errorMessage !== '' && (
                <Alert
                  className="groups-table-error-message"
                  variant="danger"
                  onClose={onErrorMessageClose}
                  dismissible
                >
                  <div>{errorMessage}</div>
                  <div>This message will disappear in 5 seconds</div>
                </Alert>
              )}
              <div
                key="Store Groups"
                style={{ display: currentTab === 'Store Groups' ? 'block' : 'none' }}
                className="groups-table"
              >
                <StoreItemTable
                  type="store"
                  onActionClick={(key, id) => {
                    return handleActionClick(key, id, 'store');
                  }}
                  error={storesError}
                  rows={
                    storesData && storesData.fetchFilterGroups ? storesData.fetchFilterGroups : []
                  }
                  showLoading={storesLoading}
                  totalRows={
                    storesData && storesData.fetchFilterGroups
                      ? storesData.fetchFilterGroups.length
                      : 0
                  }
                  onRowClick={(row) => {
                    let entity = TAB_TO_QUERY_PARAM.get(STORES_GROUPS_TYPE);
                    history.push({
                      ...getGroupDetailsNavigation(row.name, entity, row.id),
                      state: row,
                    });
                  }}
                />
              </div>
              <div
                key="Item Groups"
                style={{ display: currentTab === 'Item Groups' ? 'block' : 'none' }}
                className="groups-table"
              >
                <StoreItemTable
                  type="item"
                  onActionClick={(key, id) => handleActionClick(key, id, 'item')}
                  error={itemsError}
                  rows={itemsData && itemsData.fetchFilterGroups ? itemsData.fetchFilterGroups : []}
                  showLoading={itemsLoading}
                  totalRows={
                    itemsData && itemsData.fetchFilterGroups
                      ? itemsData.fetchFilterGroups.length
                      : 0
                  }
                  onRowClick={(row) => {
                    let entity = TAB_TO_QUERY_PARAM.get(ITEMS_GROUPS_TYPE);
                    history.push({
                      ...getGroupDetailsNavigation(row.name, entity, row.id),
                      state: row,
                    });
                  }}
                />
              </div>
            </>
          ),
        },
      }}
    </Widget>
  );
};

const mapStateToProps = (state) => ({
  clientId: clientIdSelector(state),
  currentBrand: currentBrandSelector(state),
  isEnterpriseContext: isEnterpriseContextSelector(state),
  currentTab: selectors.currentTabSelector(state),
  errorMessage: selectors.errorMessageSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onEditOpen: (id: string, groupType: GroupType) => {
    dispatch(groupModalActions.openEdit(id, groupType));
  },
  onTabSelect: (value: string) => {
    dispatch(actions.selectTab(value));
  },
  onViewGroup: (id: string) => {
    dispatch(actions.openViewGroupModal(id));
  },
  onDeleteGroup: (id: string, context: string) => {
    dispatch(actions.openDeleteConfirmationModal(id, context));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GroupsTablesWidget);
